/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import moment from "moment";
import { Button, FieldGroup, SvgIcon, Table } from "core/components";
import { ProjectFormData, PurchaseRequestStatus } from "core/schema";
import { Controller, useFieldArray } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Modal } from "react-bootstrap";

type ProjectPurchaseRequestProps = {
  project: ProjectFormData;
};

const ProjectPurchaseRequest = ({
  purchaseRequest,
  control,
  reset,
  getValues,
  setValue,
  setItemsForDestruction
}: ProjectPurchaseRequestProps): JSX.Element => {
  const fieldArrayName = "purchase_request_items";
  const { fields, remove } = useFieldArray({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [purchaseRequestItems, setPurchaseRequestItems] = React.useState(purchaseRequest.purchase_request_items);
  const [itemCount, setItemCount] = React.useState(purchaseRequest.purchase_request_items.length);
  const [showPurchaseOrdersModal, setShowPurchaseOrdersModal] = React.useState(false);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Inventory Item",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          const index = props.row.index;
          const hasPurchaseOrders = props.row.original.purchase_orders?.length > 0;
          const quantity = +props.row.original.budgeted_quantity > 0 ? +props.row.original.budgeted_quantity : +props.value;
          const maxQuantity =
            purchaseRequest.status == PurchaseRequestStatus.ForApproval ? 9999999 : purchaseRequest.approved_by_id ? +props.value : quantity;

          if ([PurchaseRequestStatus.Open, PurchaseRequestStatus.ForApproval].includes(purchaseRequest.status) && !hasPurchaseOrders) {
            return (
              <Controller
                name={`${fieldArrayName}.${index}.requested_quantity`}
                control={control}
                render={({ field, fieldState }) => (
                  <FieldGroup errorMessage={props.errors?.data?.[index]?.requested_quantity?.message}>
                    <IMaskInput
                      mask={Number}
                      padFractionalZeros={true}
                      thousandsSeparator=","
                      radix="."
                      value={field.value?.toString()}
                      unmask={true}
                      inputRef={field.ref}
                      onAccept={(value) => {
                        field.onChange(value);
                      }}
                      className="field"
                      placeholder={"0.0"}
                      min={0.1}
                      max={maxQuantity}
                      style={fieldState.error ? { borderColor: "red" } : {}}
                    />
                  </FieldGroup>
                )}
              />
            );
          }

          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value}</p>;
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "View P.O",
        accessor: "",
        Cell: (props) => {
          const purchaseOrders = props.row.original.purchase_orders;
          const hasPurchaseOrders = purchaseOrders?.length > 0;

          if (!hasPurchaseOrders) return <></>;

          return (
            <div className="d-flex justify-content-center">
              <button
                className="button button--action button--action-success"
                title={"View Purchase Orders"}
                data-bs-toggle="tooltip"
                onClick={() => {
                  setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
                  setPurchaseOrders(purchaseOrders);
                }}
              >
                <SvgIcon name="clipboard-list" className="h5" />
              </button>
            </div>
          );
        }
      },
      {
        accessor: "actions",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          const id = props.row.original.id;
          const hasPurchaseOrders = props.row.original.purchase_orders?.length > 0;

          if (
            [PurchaseRequestStatus.Open, PurchaseRequestStatus.ForApproval].includes(purchaseRequest.status) &&
            !hasPurchaseOrders &&
            getValues("item_count") > 1
          ) {
            return (
              <Button
                className="button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  if (id) {
                    const item = purchaseRequestItems.find((item) => item?.id == id);
                    if (item) setItemsForDestruction((value) => [...value, { ...item, _destroy: "1" }]);

                    setPurchaseRequestItems((items) => items.filter((item) => item.id != id));
                  }
                  setItemCount((itemCount) => itemCount - 1);
                  remove(index);
                }}
              />
            );
          } else {
            return <></>;
          }
        }
      }
    ],
    []
  );

  const purchaseOrderColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Purchase Order",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Expected Delivery Date",
        accessor: "expected_delivery_date",
        Cell: (props) => {
          if (!props.value) return <p className="text-center">-</p>;

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  React.useEffect(() => {
    if (itemCount == purchaseRequest.purchase_request_items.length) {
      setItemsForDestruction([]);
    }
    reset({
      id: purchaseRequest.id,
      purchase_request_items: purchaseRequestItems
    });
    setValue("item_count", itemCount);
  }, [itemCount]);

  return (
    <React.StrictMode>
      <Table columns={columns} data={fields} />
      <Modal dialogClassName="modal-dialog-centered modal-lg" show={showPurchaseOrdersModal}>
        <Modal.Header>{`View Purchase Orders for: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          {purchaseOrders.length > 0 ? (
            <>
              <Table columns={purchaseOrderColumns} data={purchaseOrders} />
            </>
          ) : (
            <div className="text-center mt-4 mb-4">No available purchase orders.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ProjectPurchaseRequest;
